/**
 * Bet Parser Service - Uses the existing chat API to parse bet text
 * and extract structured betting information
 */

import { findNFLTeam, matchTeamFromBetText } from './teamDatabase';

// Get API URL from environment or default
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

/**
 * Parse bet text using the AI to extract structured information
 * @param {string} betText - The raw bet text to parse
 * @returns {Promise<Object>} Parsed bet information
 */
export async function parseBetWithAI(betText) {
  
  // Construct a specific prompt that will make the LLM return structured data
  const systemPrompt = `You are a betting assistant. Parse this bet and return ONLY a JSON object with:
{
  "sport_key": "americanfootball_nfl | basketball_nba | baseball_mlb | icehockey_nhl | soccer_epl",
  "teams": ["team1", "team2"],
  "bet_type": "moneyline | spread | total | player_prop",
  "selection": "team name or over/under or player name",
  "points": null or number for spread/total/player prop line,
  "home_team": "full team name",
  "away_team": "full team name",
  "market_key": "h2h | spreads | totals | player_points | player_rebounds | player_assists | player_pass_tds | player_rush_yds | player_receptions",
  "player_name": "player full name (only for player props)",
  "player_stat_type": "points | rebounds | assists | pass_tds | rush_yds | receptions (only for player props)"
}

Examples:
Game Odds:
"Cowboys ML" -> {"sport_key": "americanfootball_nfl", "teams": ["Dallas Cowboys"], "bet_type": "moneyline", "selection": "Dallas Cowboys", "market_key": "h2h"}
"Lakers -5.5" -> {"sport_key": "basketball_nba", "teams": ["Los Angeles Lakers"], "bet_type": "spread", "selection": "Los Angeles Lakers", "points": -5.5, "market_key": "spreads"}
"Over 47.5 Chiefs vs Bills" -> {"sport_key": "americanfootball_nfl", "teams": ["Kansas City Chiefs", "Buffalo Bills"], "bet_type": "total", "selection": "over", "points": 47.5, "market_key": "totals"}

Player Props:
"Tyler Herro over 22.5 points" -> {"sport_key": "basketball_nba", "bet_type": "player_prop", "selection": "over", "points": 22.5, "player_name": "Tyler Herro", "player_stat_type": "points", "market_key": "player_points"}
"LeBron James under 8.5 rebounds" -> {"sport_key": "basketball_nba", "bet_type": "player_prop", "selection": "under", "points": 8.5, "player_name": "LeBron James", "player_stat_type": "rebounds", "market_key": "player_rebounds"}
"Patrick Mahomes over 1.5 pass TDs" -> {"sport_key": "americanfootball_nfl", "bet_type": "player_prop", "selection": "over", "points": 1.5, "player_name": "Patrick Mahomes", "player_stat_type": "pass_tds", "market_key": "player_pass_tds"}

Parse this bet: "${betText}"
Return ONLY the JSON object, no explanation.`;

  try {
    const response = await fetch(`${API_URL}/ai/ask`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        question: systemPrompt,
        history: [],
        metadata: {
          userId: 'parser',
          conversationId: 'bet-parser',
          skipSave: true // Don't save parser requests to chat history
        }
      })
    });

    if (!response.ok) {
      throw new Error(`Failed to parse bet: ${response.status}`);
    }

    // Read the streaming response
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let fullResponse = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      
      const chunk = decoder.decode(value, { stream: true });
      const lines = chunk.split('\n');
      
      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6));
            if (data.type === 'content') {
              fullResponse += data.text;
            }
          } catch (e) {
            // Ignore parse errors for incomplete chunks
          }
        }
      }
    }

    // Extract JSON from the response
    console.log('AI Parser - Full response:', fullResponse);
    
    const jsonMatch = fullResponse.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      const parsedData = JSON.parse(jsonMatch[0]);
      console.log('AI Parser - Extracted JSON:', parsedData);
      
      // Ensure sport_key is set - if missing, try to detect from teams or text
      if (!parsedData.sport_key) {
        parsedData.sport_key = detectSportFromContext(betText, parsedData.teams);
      }
      
      // Ensure market_key matches bet_type
      if (!parsedData.market_key) {
        if (parsedData.bet_type === 'moneyline') parsedData.market_key = 'h2h';
        else if (parsedData.bet_type === 'spread') parsedData.market_key = 'spreads';
        else if (parsedData.bet_type === 'total') parsedData.market_key = 'totals';
      }
      
      return parsedData;
    }

    console.error('AI Parser - Failed to extract JSON from response');
    throw new Error('Failed to extract JSON from AI response');
    
  } catch (error) {
    console.error('Error parsing bet with AI:', error);
    // Fallback to basic parsing
    return fallbackParseBet(betText);
  }
}

/**
 * Detect sport from context clues in the bet text or team names
 */
function detectSportFromContext(betText, teams = []) {
  const text = betText.toLowerCase();
  const teamsText = teams?.join(' ').toLowerCase() || '';
  const combinedText = text + ' ' + teamsText;
  
  // NFL teams/keywords
  const nflTeams = ['chiefs', 'bills', 'cowboys', 'eagles', 'patriots', 'packers', 'steelers', 'chargers', 'rams', 'bengals', 'ravens', 'browns', '49ers', 'raiders', 'broncos'];
  const nflKeywords = ['nfl', 'football', 'touchdown', 'field goal', 'quarterback'];
  if (nflTeams.some(team => combinedText.includes(team)) || nflKeywords.some(kw => combinedText.includes(kw))) {
    return 'americanfootball_nfl';
  }
  
  // NBA teams/keywords
  const nbaTeams = ['lakers', 'celtics', 'warriors', 'heat', 'nets', 'knicks', 'bulls', 'spurs', 'nuggets', 'suns', 'bucks'];
  const nbaKeywords = ['nba', 'basketball', 'points', 'rebounds', 'assists'];
  if (nbaTeams.some(team => combinedText.includes(team)) || nbaKeywords.some(kw => combinedText.includes(kw))) {
    return 'basketball_nba';
  }
  
  // MLB teams/keywords
  const mlbTeams = ['yankees', 'dodgers', 'astros', 'braves', 'red sox', 'cubs', 'giants', 'mets'];
  const mlbKeywords = ['mlb', 'baseball', 'innings', 'runs', 'pitcher'];
  if (mlbTeams.some(team => combinedText.includes(team)) || mlbKeywords.some(kw => combinedText.includes(kw))) {
    return 'baseball_mlb';
  }
  
  // NHL teams/keywords
  const nhlTeams = ['rangers', 'bruins', 'maple leafs', 'canadiens', 'penguins', 'lightning', 'avalanche'];
  const nhlKeywords = ['nhl', 'hockey', 'goals', 'period', 'puck'];
  if (nhlTeams.some(team => combinedText.includes(team)) || nhlKeywords.some(kw => combinedText.includes(kw))) {
    return 'icehockey_nhl';
  }
  
  // Default to NFL as it's most common
  console.log('Could not detect sport from context, defaulting to NFL');
  return 'americanfootball_nfl';
}

/**
 * Fallback parser for when AI parsing fails
 */
function fallbackParseBet(betText) {
  const text = betText.toLowerCase();

  // Detect sport
  let sport_key = 'americanfootball_nfl'; // default
  if (text.includes('nba') || text.includes('basketball')) {
    sport_key = 'basketball_nba';
  } else if (text.includes('mlb') || text.includes('baseball')) {
    sport_key = 'baseball_mlb';
  } else if (text.includes('nhl') || text.includes('hockey')) {
    sport_key = 'icehockey_nhl';
  } else if (text.includes('soccer') || text.includes('premier')) {
    sport_key = 'soccer_epl';
  }

  // Check for player props
  const playerPropKeywords = ['points', 'rebounds', 'assists', 'touchdowns', 'yards', 'receptions', 'goals', 'saves'];
  const isPlayerProp = playerPropKeywords.some(keyword => text.includes(keyword)) &&
                       (text.includes('over') || text.includes('under'));

  if (isPlayerProp) {
    // Player prop parsing
    let player_name = null;
    let player_stat_type = null;
    let market_key = null;

    // Extract stat type
    if (text.includes('points')) {
      player_stat_type = 'points';
      market_key = 'player_points';
    } else if (text.includes('rebounds')) {
      player_stat_type = 'rebounds';
      market_key = 'player_rebounds';
    } else if (text.includes('assists')) {
      player_stat_type = 'assists';
      market_key = 'player_assists';
    } else if (text.includes('touchdowns') || text.includes('tds')) {
      player_stat_type = 'pass_tds';
      market_key = 'player_pass_tds';
    } else if (text.includes('yards')) {
      player_stat_type = 'rush_yds';
      market_key = 'player_rush_yds';
    } else if (text.includes('receptions')) {
      player_stat_type = 'receptions';
      market_key = 'player_receptions';
    }

    // Extract points
    const pointMatch = text.match(/(\d+\.?\d*)/);
    const points = pointMatch ? parseFloat(pointMatch[1]) : null;

    // Extract over/under
    const selection = text.includes('over') ? 'over' : 'under';

    // Try to extract player name (text before "over" or "under")
    const nameMatch = betText.match(/^([A-Z][a-z]+(?:\s+[A-Z][a-z']+)*)/);
    if (nameMatch) {
      player_name = nameMatch[1].trim();
    }

    return {
      sport_key,
      bet_type: 'player_prop',
      market_key,
      points,
      selection,
      player_name,
      player_stat_type
    };
  }

  // Game odds parsing
  let bet_type = 'moneyline';
  let market_key = 'h2h';
  let points = null;

  if (text.includes('spread') || text.match(/[+-]\d+\.?\d*/)) {
    bet_type = 'spread';
    market_key = 'spreads';
    const pointMatch = text.match(/([+-]\d+\.?\d*)/);
    if (pointMatch) points = parseFloat(pointMatch[1]);
  } else if (text.includes('over') || text.includes('under') || text.includes('total')) {
    bet_type = 'total';
    market_key = 'totals';
    const totalMatch = text.match(/\d+\.?\d*/);
    if (totalMatch) points = parseFloat(totalMatch[0]);
  }

  // Extract team names (simple approach)
  const teams = [];
  const commonTeams = [
    'cowboys', 'eagles', 'giants', 'patriots', 'chiefs', 'bills',
    'lakers', 'celtics', 'warriors', 'heat', 'nets', 'bucks',
    'yankees', 'dodgers', 'astros', 'red sox', 'cubs',
    'rangers', 'lightning', 'avalanche', 'penguins'
  ];

  for (const team of commonTeams) {
    if (text.includes(team)) {
      teams.push(team.charAt(0).toUpperCase() + team.slice(1));
    }
  }

  return {
    sport_key,
    teams,
    bet_type,
    market_key,
    points,
    selection: betText
  };
}

/**
 * Enhance bet with odds data by matching parsed info to actual games
 */
export async function enhanceBetWithOdds(parsedBet) {
  console.log('Enhancing bet with odds - Input:', parsedBet);

  // Import the odds client
  const { fetchOddsWithLinks, fetchEventPlayerProps } = await import('./oddsApiClient');

  try {
    // For player props, fetch game odds first to get event ID
    if (parsedBet.bet_type === 'player_prop') {
      console.log('Player prop detected, fetching game odds first...');
      const oddsData = await fetchOddsWithLinks(parsedBet.sport_key);
      console.log('Game odds received:', oddsData?.length, 'games');

      // Find matching game
      const matchingGame = findMatchingGame(parsedBet, oddsData);
      console.log('Matching game found:', matchingGame ? 'Yes' : 'No');

      if (matchingGame) {
        console.log('Fetching player props for event:', matchingGame.id);

        // Fetch player props for this event
        try {
          const playerPropsData = await fetchEventPlayerProps(
            parsedBet.sport_key,
            matchingGame.id,
            { markets: parsedBet.market_key }
          );
          console.log('Player props received:', playerPropsData);

          // Extract bookmaker data from player props
          const bookmakerData = extractPlayerPropsBookmakerData(
            playerPropsData,
            parsedBet
          );
          console.log('Player props bookmaker data:', bookmakerData);

          return {
            ...parsedBet,
            enhanced: true,
            game: matchingGame,
            bookmakers: bookmakerData,
            isPlayerProp: true
          };
        } catch (error) {
          console.error('Error fetching player props:', error);
          // Return with game info but no player props
          return {
            ...parsedBet,
            enhanced: false,
            game: matchingGame,
            error: 'Player props not available'
          };
        }
      }
    } else {
      // Regular game odds
      console.log('Fetching game odds for sport:', parsedBet.sport_key);
      const oddsData = await fetchOddsWithLinks(parsedBet.sport_key);
      console.log('Odds data received:', oddsData?.length, 'games');

      // Find matching game
      const matchingGame = findMatchingGame(parsedBet, oddsData);
      console.log('Matching game found:', matchingGame ? 'Yes' : 'No');

      if (matchingGame) {
        console.log('About to extract bookmaker data...');
        const bookmakerData = extractBookmakerData(matchingGame, parsedBet);
        console.log('Bookmaker data extracted:', Object.keys(bookmakerData));

        return {
          ...parsedBet,
          enhanced: true,
          game: matchingGame,
          bookmakers: bookmakerData
        };
      }
    }
  } catch (error) {
    console.error('Error enhancing bet with odds:', error);
  }

  console.log('Returning unenhanced bet');
  return parsedBet;
}

/**
 * Find the game that matches the parsed bet
 */
function findMatchingGame(parsedBet, oddsData) {
  if (!oddsData || !Array.isArray(oddsData)) return null;

  const teams = parsedBet.teams || [];

  // For player props, we also need team context
  if (parsedBet.bet_type === 'player_prop' && parsedBet.player_name) {
    // Try to find game by matching player name in context
    // For now, we'll match by any team mentioned
    // TODO: Could enhance with player-to-team mapping database
  }

  for (const game of oddsData) {
    const homeTeam = game.home_team?.toLowerCase() || '';
    const awayTeam = game.away_team?.toLowerCase() || '';

    // Check if any of the parsed teams match
    for (const team of teams) {
      const teamLower = team.toLowerCase();

      // Try exact match first
      if (homeTeam === teamLower || awayTeam === teamLower) {
        return game;
      }

      // Try contains match
      if (homeTeam.includes(teamLower) || awayTeam.includes(teamLower) ||
          teamLower.includes(homeTeam) || teamLower.includes(awayTeam)) {
        return game;
      }

      // Try matching last word (team nickname)
      const homeNickname = homeTeam.split(' ').pop();
      const awayNickname = awayTeam.split(' ').pop();
      const teamNickname = teamLower.split(' ').pop();

      if (homeNickname === teamNickname || awayNickname === teamNickname) {
        return game;
      }

      // For NFL, try team database matching
      if (parsedBet.sport_key === 'americanfootball_nfl') {
        const teamMatch = findNFLTeam(team);
        if (teamMatch) {
          const fullName = teamMatch.full.toLowerCase();
          if (homeTeam === fullName || awayTeam === fullName) {
            return game;
          }
        }
      }
    }
  }

  return null;
}

/**
 * Extract bookmaker data for player props
 */
function extractPlayerPropsBookmakerData(playerPropsData, parsedBet) {
  console.log('=== EXTRACTING PLAYER PROPS BOOKMAKER DATA ===');
  console.log('Player props data:', playerPropsData);
  console.log('Parsed bet:', parsedBet);

  const bookmakerData = {};

  if (!playerPropsData.bookmakers) {
    console.log('ERROR: No bookmakers in player props data');
    return bookmakerData;
  }

  // Iterate through each bookmaker
  for (const [bookmakerKey, bookmaker] of Object.entries(playerPropsData.bookmakers)) {
    console.log(`Checking bookmaker ${bookmakerKey}`);

    // Check if this bookmaker has the player props market
    const market = bookmaker.markets?.[parsedBet.market_key];

    if (!market) {
      console.log(`No ${parsedBet.market_key} market for ${bookmakerKey}`);
      continue;
    }

    console.log(`Found market for ${bookmakerKey}:`, market);

    // Find the matching player and over/under
    let matchedOutcome = null;
    const playerName = (parsedBet.player_name || '').toLowerCase().trim();
    const selection = (parsedBet.selection || '').toLowerCase();

    // Look through outcomes to find matching player
    for (const [outcomeName, outcomeData] of Object.entries(market.outcomes || {})) {
      // Outcome name is like "LeBron James - Over" or "LeBron James - Under"
      const outcomeDescription = (outcomeData.description || '').toLowerCase().trim();
      const outcomeName_lower = (outcomeData.name || '').toLowerCase();

      console.log(`Checking outcome: ${outcomeName}`, {
        description: outcomeDescription,
        name: outcomeName_lower,
        point: outcomeData.point
      });

      // Match player name
      const playerMatches = outcomeDescription.includes(playerName) ||
                           playerName.includes(outcomeDescription);

      // Match over/under
      const selectionMatches = outcomeName_lower === selection;

      if (playerMatches && selectionMatches) {
        matchedOutcome = { name: outcomeName, ...outcomeData };
        console.log('✅ MATCHED! Player prop outcome:', matchedOutcome);
        break;
      }
    }

    if (matchedOutcome) {
      // Extract marketId from URL if sid is null
      let marketId = market.sid;
      if (!marketId && matchedOutcome.link) {
        const urlParams = new URLSearchParams(matchedOutcome.link.split('?')[1]);
        marketId = urlParams.get('marketId');
      }

      bookmakerData[bookmakerKey] = {
        bookmaker_key: bookmakerKey,
        event_id: playerPropsData.id,
        event_sid: bookmaker.event_sid,
        market_key: parsedBet.market_key,
        market_sid: marketId,
        selection_id: matchedOutcome.sid,
        selection_name: matchedOutcome.name,
        price: matchedOutcome.price,
        point: matchedOutcome.point,
        direct_link: matchedOutcome.best_link || matchedOutcome.link || bookmaker.event_link,
        home_team: playerPropsData.home_team,
        away_team: playerPropsData.away_team,
        commence_time: playerPropsData.commence_time,
        player_name: parsedBet.player_name,
        player_stat_type: parsedBet.player_stat_type
      };
      console.log(`Added bookmaker data for ${bookmakerKey}:`, bookmakerData[bookmakerKey]);
    }
  }

  console.log('Final player props bookmaker data:', bookmakerData);
  return bookmakerData;
}

/**
 * Extract bookmaker-specific data for the bet
 */
function extractBookmakerData(game, parsedBet) {
  console.log('=== EXTRACTING BOOKMAKER DATA ===');
  console.log('Game structure:', JSON.stringify(game, null, 2));
  console.log('Looking for market:', parsedBet.market_key);
  console.log('Parsed bet:', parsedBet);
  
  const bookmakerData = {};
  
  if (!game.bookmakers) {
    console.log('ERROR: No bookmakers in game data');
    console.log('Game keys:', Object.keys(game));
    return bookmakerData;
  }
  
  // Iterate through each bookmaker
  for (const [bookmakerKey, bookmaker] of Object.entries(game.bookmakers)) {
    console.log(`Checking bookmaker ${bookmakerKey}:`, bookmaker);
    
    // Check if this bookmaker has the market we're looking for
    const market = bookmaker.markets?.[parsedBet.market_key];
    
    if (!market) {
      console.log(`No ${parsedBet.market_key} market for ${bookmakerKey}`);
      continue;
    }
    
    console.log(`Found market for ${bookmakerKey}:`, market);
    
    // Find the right outcome
    let matchedOutcome = null;
    
    // Check each outcome in the market
    for (const [outcomeName, outcomeData] of Object.entries(market.outcomes || {})) {
      console.log(`Checking outcome: ${outcomeName}`, outcomeData);
      
      if (parsedBet.bet_type === 'spread') {
        // For spreads, we need to match team names intelligently
        // The selection from AI might be "Philadelphia Eagles" but the outcome might be just "Philadelphia Eagles"
        // or it could be the other way around
        
        const selectionLower = (parsedBet.selection || '').toLowerCase().trim();
        const outcomeLower = outcomeName.toLowerCase().trim();
        
        console.log(`Spread comparison - Selection: "${selectionLower}" | Outcome: "${outcomeLower}"`);
        
        // Try multiple matching strategies
        let isMatch = false;
        
        // 1. Exact match
        if (selectionLower === outcomeLower) {
          isMatch = true;
          console.log('Match type: Exact match');
        }
        
        // 2. One contains the other
        if (!isMatch && (selectionLower.includes(outcomeLower) || outcomeLower.includes(selectionLower))) {
          isMatch = true;
          console.log('Match type: Contains match');
        }
        
        // 3. Team name matching - extract key team words
        if (!isMatch) {
          // Common team name patterns to check
          const teamKeywords = ['eagles', 'cowboys', 'patriots', 'chiefs', 'bills', 'packers', 
                                'rams', 'bengals', 'ravens', 'steelers', 'browns', 'titans',
                                'lakers', 'celtics', 'warriors', 'nets', 'heat', 'bucks',
                                'yankees', 'dodgers', 'astros', 'braves', 'mets'];
          
          // Check if both strings contain the same team keyword
          for (const keyword of teamKeywords) {
            if (selectionLower.includes(keyword) && outcomeLower.includes(keyword)) {
              isMatch = true;
              console.log(`Match type: Team keyword match - "${keyword}"`);
              break;
            }
          }
        }
        
        // 4. Smart word matching - at least one significant word matches
        if (!isMatch) {
          const selectionWords = selectionLower.split(/\s+/).filter(word => word.length > 2);
          const outcomeWords = outcomeLower.split(/\s+/).filter(word => word.length > 2);
          
          // Check for any common significant words
          const hasCommonWord = selectionWords.some(word => 
            outcomeWords.some(outcomeWord => word === outcomeWord)
          );
          
          if (hasCommonWord) {
            isMatch = true;
            console.log('Match type: Common word match');
          }
        }
        
        if (isMatch) {
          matchedOutcome = { name: outcomeName, ...outcomeData };
          console.log('✅ MATCHED! Spread outcome:', matchedOutcome);
          console.log('Note: Requested spread:', parsedBet.points, 'Current spread:', outcomeData.point);
          break;
        } else {
          console.log('❌ No match for this outcome');
        }
      } else if (parsedBet.bet_type === 'moneyline') {
        // For moneyline, match team names
        const selectionLower = (parsedBet.selection || '').toLowerCase().trim();
        const outcomeLower = outcomeName.toLowerCase().trim();
        
        console.log(`Moneyline comparison - Selection: "${selectionLower}" | Outcome: "${outcomeLower}"`);
        
        if (selectionLower === outcomeLower || 
            selectionLower.includes(outcomeLower) || 
            outcomeLower.includes(selectionLower)) {
          matchedOutcome = { name: outcomeName, ...outcomeData };
          console.log('✅ MATCHED! Moneyline outcome:', matchedOutcome);
          break;
        }
      } else if (parsedBet.bet_type === 'total') {
        // For totals, we're looking for Over or Under
        const selectionLower = (parsedBet.selection || '').toLowerCase();
        const isOver = selectionLower.includes('over');
        const isUnder = selectionLower.includes('under');
        
        console.log(`Total comparison - Selection: "${selectionLower}" | Outcome: "${outcomeName}"`);
        
        if ((isOver && outcomeName.toLowerCase() === 'over') || 
            (isUnder && outcomeName.toLowerCase() === 'under')) {
          matchedOutcome = { name: outcomeName, ...outcomeData };
          console.log('✅ MATCHED! Total outcome:', matchedOutcome);
          console.log('Note: Requested total:', parsedBet.points, 'Current total:', outcomeData.point);
          break;
        }
      }
    }
    
    if (matchedOutcome) {
      // Extract marketId from URL if sid is null
      let marketId = market.sid;
      if (!marketId && matchedOutcome.link) {
        const urlParams = new URLSearchParams(matchedOutcome.link.split('?')[1]);
        marketId = urlParams.get('marketId');
      }

      bookmakerData[bookmakerKey] = {
        bookmaker_key: bookmakerKey,
        event_id: game.id,
        event_sid: bookmaker.event_sid,
        market_key: parsedBet.market_key,
        market_sid: marketId,
        selection_id: matchedOutcome.sid,
        selection_name: matchedOutcome.name,
        price: matchedOutcome.price,
        point: matchedOutcome.point,
        direct_link: matchedOutcome.best_link || matchedOutcome.link || bookmaker.link,
        home_team: game.home_team,
        away_team: game.away_team,
        commence_time: game.commence_time
      };
      console.log(`Added bookmaker data for ${bookmakerKey}:`, bookmakerData[bookmakerKey]);
    }
  }
  
  console.log('Final bookmaker data:', bookmakerData);
  return bookmakerData;
}